import { motion } from "framer-motion";
import { AnimatedPage } from "../components/AnimatedPage";
import { CTASection } from "../components/CTASection";
import { PageHero } from "../components/PageHero";
import { Section } from "../components/Section";
import {
  differentiators,
  mission,
  subsidiariesStatement,
  values,
  vision,
} from "../data/site";

export default function About() {
  return (
    <AnimatedPage>
      <PageHero
        eyebrow="About Lazerwebs"
        title="A software company built for ambitious digital transformation"
        description="Lazerwebs designs, develops, and supports websites, apps, SaaS products, enterprise platforms, and ready-made software systems for businesses that need reliable execution."
      />
      <Section align="left">
        <div className="grid gap-6 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <div>
            <p className="eyebrow">Company overview</p>
            <h2 className="section-title max-w-xl">
              We help organizations turn operations into elegant software.
            </h2>
            <p className="section-copy max-w-xl">
              Lazerwebs works with startups, companies, enterprises, and public
              organizations to plan, build, launch, and improve digital products
              that are secure, fast, and easy to use.
            </p>
            <p className="mt-6 rounded-3xl border border-electric/25 bg-electric/10 p-5 text-sm leading-7 text-white/80">
              {subsidiariesStatement}
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Statement title="Mission" body={mission} />
            <Statement title="Vision" body={vision} />
          </div>
        </div>
      </Section>
      <Section
        eyebrow="Core values"
        title="The standards behind every project"
        description="Our culture is shaped by customer success, rigorous quality, honest collaboration, and continuous improvement."
        className="bg-white/[0.025]"
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((value, index) => (
            <motion.div
              key={value}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.04 }}
              className="glass-card p-5"
            >
              <span className="text-sm font-semibold text-electric">
                0{index + 1}
              </span>
              <h3 className="mt-5 font-display text-lg font-semibold text-white">
                {value}
              </h3>
            </motion.div>
          ))}
        </div>
      </Section>
      <Section
        eyebrow="Team expertise"
        title="Strategy, design, engineering, and launch support in one place"
        description="Lazerwebs combines product insight with hands-on implementation so clients get more than screens and code. They get a practical path from idea to adoption."
      >
        <div className="grid gap-4 md:grid-cols-2">
          {differentiators.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="glass-card p-6"
              >
                <Icon className="h-7 w-7 text-electric" />
                <h3 className="mt-5 font-display text-xl font-semibold text-white">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm leading-6 text-white/60">
                  {item.description}
                </p>
              </motion.article>
            );
          })}
        </div>
      </Section>
      <CTASection />
    </AnimatedPage>
  );
}

function Statement({ title, body }: { title: string; body: string }) {
  return (
    <article className="glass-card p-6">
      <h3 className="font-display text-2xl font-semibold text-white">{title}</h3>
      <p className="mt-4 text-sm leading-7 text-white/65">{body}</p>
    </article>
  );
}
